import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingIcon,
  Cancel as CancelIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { contractorSubscriptionService } from '../services/contractorSubscriptionService';
import contractorAvailabilityService from '../services/contractorAvailabilityService';
import ContractorAvailabilityCalendar from '../components/ContractorAvailabilityCalendar';
import type { ContractorAvailability, CreateAvailabilityData } from '../services/contractorAvailabilityService';

// Global variable to track last fetched range across component remounts.
// This prevents infinite loops if the component unmounts and remounts rapidly (e.g. due to parent re-renders).
let globalLastFetchedRange = '';

export const ContractorDashboard: React.FC = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Availability state
  const [availability, setAvailability] = useState<ContractorAvailability[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState<ContractorAvailability | null>(null);
  const [availabilityForm, setAvailabilityForm] = useState<CreateAvailabilityData>({
    startTime: '',
    endTime: '',
    isRecurring: false,
    notes: '',
  });
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Track fetching state to avoid overlapping or duplicate requests
  const isFetchingAvailabilityRef = useRef(false);
  const lastFetchTimeRef = useRef<number>(0);
  const hasInitialFetchRef = useRef(false);
  
  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  useEffect(() => {
    console.log('🎯 ContractorDashboard mounted, checking token...');
    const token = localStorage.getItem('contractor_token');
    console.log('🎯 Contractor token exists:', !!token);
    fetchDashboard();
  }, []);

  // Fetch availability ONLY when tab becomes active (once per tab activation)
  useEffect(() => {
    if (activeTab === 1 && !hasInitialFetchRef.current) {
      hasInitialFetchRef.current = true;
      // Calculate current month dates
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      fetchAvailability(startDate, endDate);
    } else if (activeTab !== 1) {
      // Reset when leaving the tab
      hasInitialFetchRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching contractor jobs...');
      const data = await contractorSubscriptionService.getContractorJobs();
      console.log('✅ Dashboard data loaded:', data);
      setDashboardData(data);
    } catch (err: any) {
      console.error('❌ Error loading dashboard:', err);
      console.error('Error response:', err.response);
      if (err.response?.status === 401) {
        console.error('⚠️ 401 Unauthorized - redirecting to auth');
        setError('Authentication required. Please sign in again.');
        // Redirect to auth page after 2 seconds
        setTimeout(() => navigate('/contractor/auth'), 2000);
      } else if (err.response?.status === 403) {
        setError('Active subscription required to access dashboard. Please subscribe to view all your jobs.');
      } else {
        setError(err.response?.data?.error || 'Error loading dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewJob = (jobToken: string) => {
    navigate(`/jobs/${jobToken}`);
  };

  const fetchAvailability = useCallback(async (startDate?: Date, endDate?: Date) => {
    // Guard: if already fetching, skip
    if (isFetchingAvailabilityRef.current) {
      return;
    }

    // Calculate date range
    const start = startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const end = endDate || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
    const startDateStr = start.toISOString().split('T')[0];
    const endDateStr = end.toISOString().split('T')[0];
    const rangeKey = `${startDateStr}_${endDateStr}`;

    // Guard: skip if already fetched this range very recently (5s)
    const now = Date.now();
    if (globalLastFetchedRange === rangeKey && now - lastFetchTimeRef.current < 5000) {
      return;
    }

    try {
      isFetchingAvailabilityRef.current = true;
      setAvailabilityLoading(true);
      const data = await contractorAvailabilityService.getMyAvailability(startDateStr, endDateStr);
      setAvailability(data || []);
      globalLastFetchedRange = rangeKey;
      lastFetchTimeRef.current = now;
    } catch (err: any) {
      console.error('Error fetching availability:', err);
      globalLastFetchedRange = ''; // Allow retry on error
      setSnackbar({ open: true, message: 'Failed to load availability', severity: 'error' });
    } finally {
      setAvailabilityLoading(false);
      isFetchingAvailabilityRef.current = false;
    }
  }, []);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleAddAvailability = useCallback(async (data: CreateAvailabilityData) => {
    await contractorAvailabilityService.createAvailability(data);
    // Clear cache and refetch current month
    globalLastFetchedRange = '';
    const currentDate = new Date();
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    await fetchAvailability(startDate, endDate);
  }, [fetchAvailability]);
  
  const handleUpdateAvailability = useCallback(async (id: string, data: Partial<CreateAvailabilityData> & { isActive?: boolean }) => {
    await contractorAvailabilityService.updateAvailability(id, data);
    // Clear cache and refetch current month
    globalLastFetchedRange = '';
    const currentDate = new Date();
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    await fetchAvailability(startDate, endDate);
  }, [fetchAvailability]);
  
  const handleDeleteAvailability = useCallback(async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this availability slot?')) {
      return;
    }

    try {
      await contractorAvailabilityService.deleteAvailability(id);
      setSnackbar({ open: true, message: 'Availability deleted successfully', severity: 'success' });
      // Clear cache and refetch
      globalLastFetchedRange = '';
      const currentDate = new Date();
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      await fetchAvailability(startDate, endDate);
    } catch (err: any) {
      console.error('Error deleting availability:', err);
      setSnackbar({ open: true, message: 'Failed to delete availability', severity: 'error' });
    }
  }, [fetchAvailability]);

  const handleOpenAvailabilityDialog = (item?: ContractorAvailability) => {
    if (item) {
      setEditingAvailability(item);
      setAvailabilityForm({
        startTime: new Date(item.startTime).toISOString().slice(0, 16),
        endTime: new Date(item.endTime).toISOString().slice(0, 16),
        isRecurring: item.isRecurring,
        recurringPattern: item.recurringPattern,
        notes: item.notes || '',
      });
      setSelectedDays(item.recurringPattern?.daysOfWeek || []);
      setIsBusy(!item.isActive); // Busy = not active
    } else {
      setEditingAvailability(null);
      setAvailabilityForm({
        startTime: '',
        endTime: '',
        isRecurring: false,
        notes: '',
      });
      setSelectedDays([]);
      setIsBusy(false);
    }
    setAvailabilityDialogOpen(true);
  };

  const handleSaveAvailability = async () => {
    try {
      if (!availabilityForm.startTime || !availabilityForm.endTime) {
        setSnackbar({ open: true, message: 'Start time and end time are required', severity: 'error' });
        return;
      }

      if (availabilityForm.isRecurring && selectedDays.length === 0) {
        setSnackbar({ open: true, message: 'Please select at least one day for recurring availability', severity: 'error' });
        return;
      }

      const formData: CreateAvailabilityData = {
        startTime: new Date(availabilityForm.startTime).toISOString(),
        endTime: new Date(availabilityForm.endTime).toISOString(),
        isRecurring: availabilityForm.isRecurring,
        notes: availabilityForm.notes || undefined,
      };

      if (availabilityForm.isRecurring) {
        formData.recurringPattern = {
          type: 'weekly',
          interval: 1,
          daysOfWeek: selectedDays,
        };
      }

      if (editingAvailability) {
        // Include isActive flag (busy = false, available = true)
        await contractorAvailabilityService.updateAvailability(editingAvailability._id, {
          ...formData,
          isActive: !isBusy
        });
        setSnackbar({ open: true, message: 'Availability updated successfully', severity: 'success' });
      } else {
        // Include isActive flag (busy = false, available = true)
        await contractorAvailabilityService.createAvailability({
          ...formData,
          isActive: !isBusy
        });
        setSnackbar({ open: true, message: isBusy ? 'Busy slot added successfully' : 'Availability added successfully', severity: 'success' });
      }

      setAvailabilityDialogOpen(false);
    // Clear cache and refetch
    globalLastFetchedRange = '';
    fetchAvailability();
    } catch (err: any) {
      console.error('Error saving availability:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save availability', severity: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'in_progress':
        return <PendingIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'cancelled':
        return <CancelIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboardData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Go to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            🔧 Contractor Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {dashboardData?.contractor?.name || 'Contractor'}!
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate('/contractor/subscription')}
        >
          Manage Subscription
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Jobs
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {dashboardData?.totalJobs || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Jobs
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {dashboardData?.jobs?.filter((j: any) => j.status === 'in_progress' || j.status === 'pending').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {dashboardData?.jobs?.filter((j: any) => j.status === 'completed').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Subscription
              </Typography>
              <Chip 
                label={dashboardData?.contractor?.subscriptionStatus === 'free' ? 'Free Plan' : dashboardData?.contractor?.subscriptionStatus || 'Unknown'} 
                color={dashboardData?.contractor?.subscriptionStatus === 'free' ? 'default' : 'success'} 
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<PendingIcon />} label="Jobs" />
          <Tab icon={<CalendarIcon />} label="Availability" />
        </Tabs>
      </Card>

      {/* Jobs Tab */}
      {activeTab === 0 && (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            All Jobs
          </Typography>
          {dashboardData?.jobs && dashboardData.jobs.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Property Address</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>Finish Date</TableCell>
                    <TableCell align="center">Photos</TableCell>
                    <TableCell align="center">Floor Plans</TableCell>
                    <TableCell align="center">Messages</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData.jobs.map((job: any) => (
                    <TableRow key={job._id} hover>
                      <TableCell>
                        {job.property?.address 
                          ? `${job.property.address.street}, ${job.property.address.city}, ${job.property.address.state} ${job.property.address.postalCode}`
                          : 'Address Hidden'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={job.contractorType} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(job.status)}
                          label={job.status.replace('_', ' ')}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {job.startDate ? new Date(job.startDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                      </TableCell>
                      <TableCell>
                        {job.finishDate ? new Date(job.finishDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                      </TableCell>
                      <TableCell align="center">{job.photosCount}</TableCell>
                      <TableCell align="center">{job.floorPlansCount}</TableCell>
                      <TableCell align="center">{job.messagesCount}</TableCell>
                      <TableCell>
                        {new Date(job.createdAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Job">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewJob(job.accessToken)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No jobs found. You'll see your jobs here once agencies assign work to you.
            </Alert>
          )}
        </CardContent>
      </Card>
      )}

      {/* Availability Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                My Availability Calendar
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenAvailabilityDialog()}
              >
                Add Recurring Availability
              </Button>
            </Box>

            {availabilityLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <ContractorAvailabilityCalendar
                availability={availability}
                onAddAvailability={handleAddAvailability}
                onUpdateAvailability={handleUpdateAvailability}
                onDeleteAvailability={handleDeleteAvailability}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Availability Dialog */}
      <Dialog open={availabilityDialogOpen} onClose={() => setAvailabilityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAvailability ? 'Edit Availability' : 'Add Availability'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={availabilityForm.startTime ? availabilityForm.startTime.split('T')[0] : ''}
                onChange={(e) => {
                  const time = availabilityForm.startTime ? availabilityForm.startTime.split('T')[1] : '09:00';
                  setAvailabilityForm({ ...availabilityForm, startTime: `${e.target.value}T${time}` });
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="Start Time"
                type="time"
                value={availabilityForm.startTime ? availabilityForm.startTime.split('T')[1] : ''}
                onChange={(e) => {
                  const date = availabilityForm.startTime ? availabilityForm.startTime.split('T')[0] : new Date().toISOString().split('T')[0];
                  setAvailabilityForm({ ...availabilityForm, startTime: `${date}T${e.target.value}` });
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="End Date"
                type="date"
                value={availabilityForm.endTime ? availabilityForm.endTime.split('T')[0] : ''}
                onChange={(e) => {
                  const time = availabilityForm.endTime ? availabilityForm.endTime.split('T')[1] : '17:00';
                  setAvailabilityForm({ ...availabilityForm, endTime: `${e.target.value}T${time}` });
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="End Time"
                type="time"
                value={availabilityForm.endTime ? availabilityForm.endTime.split('T')[1] : ''}
                onChange={(e) => {
                  const date = availabilityForm.endTime ? availabilityForm.endTime.split('T')[0] : new Date().toISOString().split('T')[0];
                  setAvailabilityForm({ ...availabilityForm, endTime: `${date}T${e.target.value}` });
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={isBusy}
                  onChange={(e) => setIsBusy(e.target.checked)}
                />
              }
              label="Mark as Busy (Unavailable)"
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={availabilityForm.isRecurring}
                  onChange={(e) => {
                    setAvailabilityForm({ ...availabilityForm, isRecurring: e.target.checked });
                    if (!e.target.checked) {
                      setSelectedDays([]);
                    }
                  }}
                />
              }
              label="Recurring availability (weekly)"
            />
            
            {availabilityForm.isRecurring && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Select Days of Week
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {daysOfWeek.map((day) => (
                    <Chip
                      key={day.value}
                      label={day.label}
                      onClick={() => {
                        if (selectedDays.includes(day.value)) {
                          setSelectedDays(selectedDays.filter(d => d !== day.value));
                        } else {
                          setSelectedDays([...selectedDays, day.value].sort());
                        }
                      }}
                      color={selectedDays.includes(day.value) ? 'primary' : 'default'}
                      variant={selectedDays.includes(day.value) ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
                {selectedDays.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    Please select at least one day
                  </Typography>
                )}
              </Box>
            )}
            
            <TextField
              label="Notes (Optional)"
              value={availabilityForm.notes}
              onChange={(e) => setAvailabilityForm({ ...availabilityForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailabilityDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAvailability} variant="contained">
            {editingAvailability ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<HomeIcon />}
          onClick={() => navigate('/')}
        >
          Back to Home
        </Button>
      </Box>
    </Container>
  );
};

export default ContractorDashboard;
